import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftRight, CircleCheck, ExternalLink, ScanBarcode, ShieldAlert, Timer, Trash2, Undo2 } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { AssetStatusBadge, DueBadge } from '../components/badges';
import { BatchCheckout, DueDateField, type LookupAsset } from '../components/batch-checkout';
import { alertGate, BorrowerAlertsPanel, useBorrowerAlerts } from '../components/borrower-alerts';
import { CheckinExtrasFields, useCheckinExtras } from '../components/checkin-extras';
import { ReportIncidentModal } from '../components/device-history';
import { LendLoanerModal } from '../components/loaner-dialog';
import { AssigneePicker, assigneeToInput, HomeLocationQuestion, useHomeLocationAnswer, type AssigneeValue } from '../components/pickers';
import { useToast } from '../components/toast';
import { Alert, Badge, Button, Card, CardHeader, Field, Input, PageHeader, Select, Tabs, Textarea } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { alertsFromError, daysLate, homeLocationNote, lateLabel, type BulkCheckinResult, type HomeLocationResult } from '../lib/checkouts';
import { addDays, CONDITION_LABEL, dueDateInputToIso, formatDateTime, toDateInput } from '../lib/format';
import { invalidateInventory } from '../lib/hooks';
import type { AssetCondition, Checkout } from '../lib/types';

type Mode = 'single' | 'batch-out' | 'batch-in';

interface LogEntry {
  at: Date;
  kind: 'out' | 'in';
  text: string;
}

const lateNote = (days: number | null) => (days === null ? '' : ` (${lateLabel(days).toLowerCase()})`);

export function CheckoutStationPage() {
  const { me, can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const scanRef = useRef<HTMLInputElement>(null);

  const [mode, setMode] = useState<Mode>('single');
  const [scan, setScan] = useState('');
  const [lookupError, setLookupError] = useState('');
  const [current, setCurrent] = useState<LookupAsset | null>(null);
  const [batch, setBatch] = useState<LookupAsset[]>([]);
  const [log, setLog] = useState<LogEntry[]>([]);

  const [assignee, setAssignee] = useState<AssigneeValue>({ type: 'PERSON', person: null });
  const [keepAssignee, setKeepAssignee] = useState(false);
  // Kept with the assignee, so a run of devices for the same room is asked once.
  const home = useHomeLocationAnswer(assignee);
  const [dueDate, setDueDate] = useState(toDateInput(addDays(me?.tenant?.defaultCheckoutDays ?? 14)));
  const [condition, setCondition] = useState<AssetCondition | ''>('');
  const [returnStatus, setReturnStatus] = useState('AVAILABLE');
  const [notes, setNotes] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [lending, setLending] = useState(false);
  const [damaged, setDamaged] = useState<{ id: string; assetTag: string; name: string } | null>(null);
  const [reporting, setReporting] = useState(false);
  const extras = useCheckinExtras();

  // Alerts on the person borrowing, before anything goes out to them.
  const personId = assignee.type === 'PERSON' ? assignee.person?.id : undefined;
  const outAlerts = useBorrowerAlerts(personId, { kind: 'STANDARD', enabled: mode === 'single' });
  useEffect(() => setConfirmed(false), [personId]);
  const gate = alertGate(personId ? outAlerts.data : [], confirmed, can('ADMIN'));
  const alertsReady = !personId || (!outAlerts.isLoading && gate.ok);

  // And on whoever is returning the device being checked in.
  const returning = mode === 'single' && current?.status === 'CHECKED_OUT' ? current.activeCheckout : null;
  const inAlerts = useBorrowerAlerts(returning?.personId, { checkoutId: returning?.id });

  const focusScan = () => {
    setTimeout(() => scanRef.current?.focus(), 0);
  };
  useEffect(focusScan, [mode]);

  const record = (kind: LogEntry['kind'], text: string) => setLog((entries) => [{ at: new Date(), kind, text }, ...entries].slice(0, 25));

  const resetForm = () => {
    setCurrent(null);
    setNotes('');
    setCondition('');
    setReturnStatus('AVAILABLE');
    setConfirmed(false);
    extras.reset();
    if (!keepAssignee) home.reset();
    if (!keepAssignee) setAssignee({ type: assignee.type, ...(assignee.type === 'PERSON' ? { person: null } : assignee.type === 'ROOM' ? { room: null } : assignee.type === 'BUILDING' ? { buildingId: '' } : { label: '' }) } as AssigneeValue);
    focusScan();
  };

  const lookup = useMutation({
    mutationFn: (code: string) => api.get<LookupAsset>('/assets/lookup', { q: code }),
    onSuccess: (asset) => {
      setLookupError('');
      setScan('');
      if (mode === 'single') {
        setCurrent(asset);
        return;
      }
      if (asset.status !== 'CHECKED_OUT') {
        setLookupError(`${asset.assetTag} is ${asset.status.replace('_', ' ').toLowerCase()} - it can’t be checked in.`);
        return;
      }
      setBatch((items) => (items.some((i) => i.id === asset.id) ? items : [...items, asset]));
    },
    onError: (err) => {
      setLookupError(errorMessage(err));
      setScan('');
    },
  });

  const submitScan = (event: FormEvent) => {
    event.preventDefault();
    const code = scan.trim();
    if (code) lookup.mutate(code);
  };

  const checkout = useMutation({
    mutationFn: (assets: LookupAsset[]) =>
      api.post<{ checkouts: Checkout[]; home: HomeLocationResult | null }>('/checkouts', {
        assetIds: assets.map((a) => a.id),
        assignee: assigneeToInput(assignee),
        dueAt: dueDateInputToIso(dueDate),
        condition: condition || undefined,
        notes: notes || undefined,
        overrideAlerts: gate.override,
        ...home.body(),
      }),
    onSuccess: ({ checkouts, home: moved }) => {
      invalidateInventory(queryClient);
      const label = checkouts[0]?.assigneeLabel;
      record('out', `${checkouts.map((c) => c.asset.assetTag).join(', ')} → ${label}${homeLocationNote(moved)}`);
      toast.success(`Checked out ${checkouts.length === 1 ? checkouts[0].asset.assetTag : `${checkouts.length} assets`} to ${label}${homeLocationNote(moved)}`);
      resetForm();
    },
    onError: (err) => {
      if (alertsFromError(err)) void outAlerts.refetch();
      toast.error(errorMessage(err));
    },
  });

  const checkin = useMutation({
    mutationFn: (asset: LookupAsset) =>
      api.post<Checkout>(`/checkouts/${asset.activeCheckout!.id}/checkin`, {
        condition: condition || undefined,
        notes: notes || undefined,
        assetStatus: returnStatus,
        ...extras.body(),
      }),
    onSuccess: (returned) => {
      invalidateInventory(queryClient);
      record('in', `${returned.asset.assetTag} ← ${returned.assigneeLabel}${lateNote(daysLate(returned))}`);
      toast.success(`Checked in ${returned.asset.assetTag}`);
      // Damage gets an incident, so it stays in the device's history.
      if (returned.conditionIn === 'DAMAGED') setDamaged({ id: returned.asset.id, assetTag: returned.asset.assetTag, name: returned.asset.name });
      resetForm();
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const batchCheckin = useMutation({
    mutationFn: (assets: LookupAsset[]) =>
      api.post<BulkCheckinResult>('/checkouts/bulk/checkin', {
        checkoutIds: assets.flatMap((asset) => (asset.activeCheckout ? [asset.activeCheckout.id] : [])),
        condition: condition || undefined,
        notes: notes || undefined,
        assetStatus: returnStatus,
      }),
    onSuccess: ({ checkedIn, failed }) => {
      invalidateInventory(queryClient);
      checkedIn.forEach((row) => record('in', `${row.assetTag} ← ${row.assigneeLabel}${lateNote(row.daysLate)}`));
      if (checkedIn.length > 0) toast.success(`Checked in ${checkedIn.length === 1 ? checkedIn[0].assetTag : `${checkedIn.length} assets`}`);
      if (failed.length > 0) toast.error(`${failed.length} couldn’t be checked in: ${failed[0].error}`);
      // What couldn't be checked in stays on the list.
      setBatch((items) => items.filter((asset) => failed.some((row) => row.checkoutId === asset.activeCheckout?.id)));
      resetForm();
    },
    onError: (err) => {
      invalidateInventory(queryClient);
      toast.error(errorMessage(err));
    },
  });

  const assigneeReady = !!assigneeToInput(assignee) && home.ready;

  const checkoutFields = (
    <div className="space-y-4">
      <Field as="div" label="Assign to" required>
        <AssigneePicker inline value={assignee} onChange={setAssignee} />
      </Field>
      {home.room && <HomeLocationQuestion room={home.room} value={home.value} onChange={home.set} />}
      <BorrowerAlertsPanel alerts={personId ? outAlerts.data : undefined} loading={!!personId && outAlerts.isLoading} confirmed={confirmed} onConfirm={setConfirmed} />
      <label className="flex items-center gap-2 text-sm text-slate-600">
        <input type="checkbox" checked={keepAssignee} onChange={(e) => setKeepAssignee(e.target.checked)} className="size-4 rounded border-slate-300" />
        Keep this assignee for the next scan
      </label>
      <DueDateField value={dueDate} onChange={setDueDate} />
      <Field label="Notes">
        <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>
    </div>
  );

  const checkinFields = (single: boolean) => (
    <div className="space-y-4">
      {single && <BorrowerAlertsPanel alerts={inAlerts.data} mode="checkin" />}
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Returned condition">
          <Select value={condition} onChange={(e) => setCondition(e.target.value as AssetCondition | '')}>
            <option value="">Not inspected</option>
            {Object.entries(CONDITION_LABEL).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Then mark the asset">
          <Select value={returnStatus} onChange={(e) => setReturnStatus(e.target.value)}>
            <option value="AVAILABLE">Available</option>
            <option value="IN_REPAIR">In repair</option>
            <option value="LOST">Lost</option>
            <option value="STOLEN">Stolen</option>
            <option value="RETIRED">Retired</option>
          </Select>
        </Field>
        <Field label="Notes" className="sm:col-span-2">
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      {single ? (
        <>
          <CheckinExtrasFields extras={extras} />
          {extras.missing.length > 0 && <p className="text-xs text-slate-500">Answer {extras.missing.join(', ')} to check it in.</p>}
        </>
      ) : (
        extras.questions.length > 0 && (
          <p className="text-xs text-slate-500">Batch check-ins skip the check-in form and photos. Check a device in on its own to answer them.</p>
        )
      )}
    </div>
  );

  return (
    <>
      <PageHeader
        title="Check in / out"
        description="Scan a barcode or type an asset tag or serial number. USB and Bluetooth scanners work out of the box."
      />

      <Tabs
        className="mb-6"
        value={mode}
        onChange={(value) => {
          setMode(value);
          setBatch([]);
          setCurrent(null);
          setLookupError('');
          // Nothing carries over from the other mode.
          setCondition('');
          setReturnStatus('AVAILABLE');
          setNotes('');
          setConfirmed(false);
          extras.reset();
        }}
        tabs={[
          { value: 'single', label: 'One at a time' },
          { value: 'batch-out', label: 'Batch check out' },
          { value: 'batch-in', label: 'Batch check in' },
        ]}
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          {mode === 'batch-out' ? (
            <BatchCheckout onCheckedOut={(text) => record('out', text)} />
          ) : (
            <>
              <Card className="p-5">
                <form onSubmit={submitScan} className="flex gap-2">
                  <div className="relative flex-1">
                    <ScanBarcode className="pointer-events-none absolute top-1/2 left-3 size-5 -translate-y-1/2 text-slate-400" />
                    <Input
                      ref={scanRef}
                      value={scan}
                      onChange={(e) => setScan(e.target.value)}
                      placeholder={mode === 'batch-in' ? 'Scan assets to return' : 'Scan or type asset tag / serial'}
                      className="h-12 pl-11 font-mono text-base"
                      autoComplete="off"
                      autoFocus
                    />
                  </div>
                  <Button type="submit" variant="primary" className="h-12 px-5" loading={lookup.isPending}>
                    Look up
                  </Button>
                </form>
                {lookupError && (
                  <Alert tone="amber" className="mt-3">
                    {lookupError}
                  </Alert>
                )}
              </Card>

              {damaged && (
                <Alert tone="amber" icon={<ShieldAlert />} title={`${damaged.assetTag} came back damaged`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span>Report an incident to keep the damage in the device’s history.</span>
                    <Button size="sm" onClick={() => setReporting(true)}>
                      Report an incident
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setDamaged(null)}>
                      Dismiss
                    </Button>
                  </div>
                </Alert>
              )}

              {mode === 'single' && current && (
                <Card>
                  <CardHeader
                    title={
                      <span className="flex items-center gap-2">
                        <span className="font-mono">{current.assetTag}</span> {current.name}
                        {current.loanerPool && <Badge tone="violet">Loaner</Badge>}
                      </span>
                    }
                    description={[current.category?.name, current.serialNumber].filter(Boolean).join(' · ')}
                    actions={
                      <>
                        <AssetStatusBadge status={current.status} />
                        <Link to={`/assets/${current.id}`} className="text-slate-400 hover:text-slate-700" title="Open asset">
                          <ExternalLink className="size-4" />
                        </Link>
                      </>
                    }
                  />
                  <div className="p-5">
                    {current.status === 'CHECKED_OUT' && current.activeCheckout ? (
                      <div className="space-y-5">
                        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-sky-50 px-4 py-3 text-sm">
                          <span>
                            Checked out to <strong>{current.activeCheckout.assigneeLabel}</strong>
                          </span>
                          <DueBadge dueAt={current.activeCheckout.dueAt} />
                        </div>
                        {checkinFields(true)}
                        <div className="flex justify-end gap-2">
                          <Button onClick={resetForm}>Cancel</Button>
                          <Button variant="primary" icon={<Undo2 />} disabled={!extras.ready} loading={checkin.isPending} onClick={() => checkin.mutate(current)}>
                            {extras.uploading ? 'Uploading photos…' : 'Check in'}
                          </Button>
                        </div>
                      </div>
                    ) : current.status === 'AVAILABLE' ? (
                      <div className="space-y-5">
                        {current.loanerPool && (
                          <Alert tone="blue" icon={<Timer />} title="This is a loaner">
                            Lend it as a loaner to track when it’s due back and send reminders.
                          </Alert>
                        )}
                        {checkoutFields}
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button onClick={resetForm}>Cancel</Button>
                          {current.loanerPool && (
                            <Button icon={<Timer />} onClick={() => setLending(true)}>
                              Lend as a loaner
                            </Button>
                          )}
                          <Button
                            variant="primary"
                            icon={<ArrowLeftRight />}
                            disabled={!assigneeReady || !alertsReady}
                            loading={checkout.isPending}
                            onClick={() => checkout.mutate([current])}
                          >
                            Check out
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <Alert tone="amber" title="This asset can’t be checked out right now">
                        It’s marked {current.status.replace('_', ' ').toLowerCase()}. Update its status from the asset page first.
                      </Alert>
                    )}
                  </div>
                </Card>
              )}

              {mode === 'batch-in' && (
                <Card>
                  <CardHeader
                    title="Assets to check in"
                    description={`${batch.length} scanned`}
                    actions={batch.length > 0 && <Button size="sm" variant="ghost" onClick={() => setBatch([])}>Clear</Button>}
                  />
                  <div className="p-5">
                    {batch.length === 0 ? (
                      <p className="text-sm text-slate-500">Scan assets to add them here.</p>
                    ) : (
                      <ul className="mb-5 divide-y divide-slate-100 rounded-lg border border-slate-200">
                        {batch.map((asset) => (
                          <li key={asset.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                            <span className="w-24 font-mono text-xs font-semibold text-brand-800">{asset.assetTag}</span>
                            <span className="flex-1 truncate">{asset.name}</span>
                            {asset.activeCheckout && <span className="truncate text-xs text-slate-500">{asset.activeCheckout.assigneeLabel}</span>}
                            <button onClick={() => setBatch((items) => items.filter((i) => i.id !== asset.id))} className="text-slate-400 hover:text-red-600" aria-label="Remove">
                              <Trash2 className="size-4" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    {batch.length > 0 && (
                      <div className="space-y-5">
                        {checkinFields(false)}
                        <div className="flex justify-end">
                          <Button variant="primary" icon={<Undo2 />} loading={batchCheckin.isPending} onClick={() => batchCheckin.mutate(batch)}>
                            Check in {batch.length} asset{batch.length === 1 ? '' : 's'}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>

        <Card className="h-fit">
          <CardHeader title="This session" description="Transactions from this station" />
          {log.length === 0 ? (
            <p className="px-5 py-6 text-sm text-slate-500">Nothing yet.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {log.map((entry, index) => (
                <li key={index} className="flex items-start gap-3 px-5 py-3 text-sm">
                  <CircleCheck className="mt-0.5 size-4 shrink-0 text-emerald-600" />
                  <div className="min-w-0">
                    <p className="text-slate-800">
                      <Badge tone={entry.kind === 'out' ? 'blue' : 'green'} className="mr-1.5">
                        {entry.kind === 'out' ? 'Out' : 'In'}
                      </Badge>
                      {entry.text}
                    </p>
                    <p className="text-xs text-slate-400">{formatDateTime(entry.at)}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      {current && lending && (
        <LendLoanerModal
          open
          onClose={() => setLending(false)}
          asset={current}
          person={assignee.type === 'PERSON' ? assignee.person : null}
          onDone={(lent) => {
            record('out', `${lent.asset.assetTag} → ${lent.assigneeLabel} (loaner)`);
            resetForm();
          }}
        />
      )}
      {damaged && (
        <ReportIncidentModal
          open={reporting}
          onClose={() => {
            setReporting(false);
            setDamaged(null);
          }}
          asset={damaged}
          stay
        />
      )}
    </>
  );
}
